// `observableQuery` — reactive parameterised read primitive
// (wiki/feature-frontend-cqrs-dataflow.md §2a).
//
// Binds a reactive parameter getter to a live list of generated read models,
// re-reading on two triggers:
//   1. params() changes → re-dispatch POST /api/query;
//   2. the watched read model gets a server-side push → refetch (via liveReadModel).
//
// Usage (inside a Svelte component):
//   let paramsState = $state<FindReviewsByUserQuery>({});
//   const q = observableQuery(findReviewsByUser, () => paramsState);
//   onMount(() => () => q.stop());
//   // q.data: AiReview[]   q.loading   q.error   q.refetch()   q.stop()
//
// ARCHITECTURE — two layers:
//
//   ObservableQueryHandle (class, defined below):
//     Owns the dispatch + liveReadModel composition. Param changes are applied
//     via `applyParams(p)`, which is called by the $effect below. The class is
//     directly testable because it never calls $effect internally — tests call
//     `applyParams()` directly.
//
//   observableQuery() factory:
//     Wraps the handle in a $effect.root() that tracks params() and calls
//     applyParams() on change. This wires the reactive tracking layer that
//     only works inside a Svelte context (component or $effect.root()). When
//     used in tests, callers can import ObservableQueryHandle and drive it
//     directly via applyParams().
//
// The socket lifecycle, auth gating, coalescing, and reconnect re-hydrate are
// fully delegated to liveReadModel — no socket logic here.
//
// `null` params → IDLE: no dispatch, data stays [].

import { api } from './client';
import { ApiError } from './client';
import { liveReadModel, type LiveReadModel, type IsAuthenticatedGate } from './liveReadModel.svelte';
import type { ReadModelsHub } from './readModelsHub';
import type { QueryDef } from '@voicedojo/api';

/** Optional construction seams (test injection + tuning). */
export type ObservableQueryOptions = {
  /**
   * Trailing-edge debounce (ms) passed to `liveReadModel` for push coalescing.
   * Defaults to 250ms.
   */
  coalesceMs?: number;
  /**
   * Detail-page filter: only a push whose `entityId` matches triggers a refetch.
   * Omit for list-page (any push for the watched model refetches).
   */
  matchId?: (entityId: string) => boolean;
  /** Injectable hub — defaults to the shared `readModelsHub` (overridden in tests). */
  hub?: ReadModelsHub;
  /** Injectable auth gate — defaults to the auth store (overridden in tests). */
  isAuthenticated?: IsAuthenticatedGate;
};

/**
 * The reactive handle returned to callers. All fields are reactive ($state)
 * so a Svelte component renders live updates without manual subscriptions.
 */
export type ObservableQueryResult<TReadModel> = {
  /** The current list of read models. Empty while idle or loading. */
  readonly data: TReadModel[];
  /** True while a dispatch is in-flight. */
  readonly loading: boolean;
  /** The last dispatch error, or null. Cleared on the next successful dispatch. */
  readonly error: ApiError | null;
  /** Force a re-dispatch with the current params. No-op when params is null. */
  refetch(): void;
  /** Tear down the liveReadModel subscription and dispose the reactive scope. Idempotent. */
  stop(): void;
};

/**
 * Reactive parameterised read primitive. Wraps `ObservableQueryHandle` with a
 * `$effect.root()` that tracks `params()` and re-dispatches on change. Call
 * this inside a Svelte component (`<script>` or `onMount`); the `$effect.root`
 * gives it a standalone reactive scope that survives the component lifetime.
 *
 * For unit tests, use `new ObservableQueryHandle(def, options)` directly and
 * drive param changes via `handle.applyParams(p)`.
 *
 * @param def     — generated QueryDef carrying the $type and watch key.
 * @param params  — reactive getter: returns the query params object, or
 *                  `null` for idle (no dispatch).
 * @param options — optional tuning / test seams.
 */
export function observableQuery<TParam extends object, TReadModel>(
  def: QueryDef<TParam, TReadModel>,
  params: () => TParam | null,
  options: ObservableQueryOptions = {},
): ObservableQueryResult<TReadModel> {
  const handle = new ObservableQueryHandle<TParam, TReadModel>(def, options);

  // $effect.root() creates a standalone reactive scope (Svelte 5) so this
  // works outside of a component. The $effect inside it tracks params() and
  // re-dispatches when it changes.
  const disposeRoot = $effect.root(() => {
    $effect(() => {
      handle.applyParams(params());
    });
  });

  const originalStop = handle.stop.bind(handle);
  handle.stop = () => {
    originalStop();
    disposeRoot();
  };

  return handle;
}

/**
 * Core reactive handle — testable without a Svelte component context.
 *
 * Drives `liveReadModel` for push reactivity and exposes `applyParams()` for
 * param-change reactivity. `observableQuery()` wraps this with a `$effect`.
 *
 * @example (in tests)
 * ```ts
 * const handle = new ObservableQueryHandle(fakeDef, { hub, isAuthenticated });
 * handle.applyParams({ userId: 'u1' });   // triggers start() + initial dispatch
 * await vi.runAllTimersAsync();
 * expect(handle.data).toEqual([...]);
 * handle.stop();
 * ```
 */
export class ObservableQueryHandle<TParam extends object, TReadModel> {
  // Reactive state — `$state` class fields are transformed by the Svelte
  // preprocessor and work correctly even in vitest (unlike `$effect`).
  #data = $state<TReadModel[]>([]);
  #loading = $state(false);
  #error = $state<ApiError | null>(null);

  // Mutable current-params ref — closed over by liveReadModel's `load` so
  // push-driven refetches always dispatch with the latest params.
  #currentParams: TParam | null = null;

  readonly #live: LiveReadModel<TReadModel[]>;
  #started = false;

  constructor(
    private readonly def: QueryDef<TParam, TReadModel>,
    options: ObservableQueryOptions = {},
  ) {
    // `matchId` resolution — entity-precise watch (wiki §2a "Watch granularity"):
    // 1. If the caller supplies an explicit `options.matchId`, it wins (rare override).
    // 2. If the def carries a `key` (entity-keyed query like `findSessionById`),
    //    auto-derive `matchId` from the current params so only the on-screen entity
    //    triggers a refetch. The lambda closes over `this.#currentParams` — always
    //    the latest value, set by `applyParams()` before any push can arrive.
    // 3. Otherwise no matchId — any owner push for the model refetches (list pages).
    //
    // NEVER use the admin `watchEntityId` path here — we always stay on
    // `WatchOwner(model)` (own-data-only), so cross-user fan-out is structurally
    // impossible (wiki §2a "never cross-user").
    const resolvedMatchId: ((entityId: string) => boolean) | undefined =
      options.matchId ??
      (def.key !== undefined
        ? (eid) => {
            const p = this.#currentParams;
            if (p === null) return false;
            const paramVal = (p as Record<string, unknown>)[def.key!];
            return paramVal !== undefined && eid === String(paramVal);
          }
        : undefined);

    this.#live = liveReadModel<TReadModel[]>({
      model: def.watch,
      load: () => this.#dispatch(),
      coalesceMs: options.coalesceMs,
      matchId: resolvedMatchId,
      hub: options.hub,
      isAuthenticated: options.isAuthenticated,
    });
  }

  get data(): TReadModel[] {
    return this.#data;
  }

  get loading(): boolean {
    return this.#loading;
  }

  get error(): ApiError | null {
    return this.#error;
  }

  /**
   * Apply new params and re-dispatch. Called automatically by the `$effect`
   * in `observableQuery()` on param change; call directly in tests.
   *
   * - `null` → idle: clear state, no dispatch.
   * - non-null on first call → `start()` (initial hydrate + subscribe).
   * - non-null on subsequent calls → `refetch()` (re-dispatch with new params).
   */
  applyParams(p: TParam | null): void {
    this.#currentParams = p;

    if (p === null) {
      this.#data = [];
      this.#loading = false;
      this.#error = null;
      if (!this.#started) {
        // Start anyway (subscribes to push channel) so a future non-null param
        // can trigger a refetch without re-starting. The initial dispatch() will
        // return [] immediately since currentParams is null.
        this.#started = true;
        void this.#live.start();
      }
      return;
    }

    if (!this.#started) {
      this.#started = true;
      // First non-null params: start() does the initial hydrate + subscribes.
      void this.#live.start();
    } else {
      // Params changed after initial start: refetch with new params.
      void this.#live.refetch();
    }
  }

  refetch(): void {
    if (this.#currentParams !== null) {
      void this.#live.refetch();
    }
  }

  stop(): void {
    this.#live.stop();
  }

  async #dispatch(): Promise<TReadModel[]> {
    const p = this.#currentParams;
    if (p === null) return [];
    this.#loading = true;
    this.#error = null;
    try {
      const rows = await api.post<TReadModel[]>('/query', {
        $type: this.def.$type,
        ...p,
      });
      this.#data = rows;
      return rows;
    } catch (e) {
      if (e instanceof ApiError) {
        this.#error = e;
      } else {
        this.#error = new ApiError(0, 'Unknown error', undefined);
      }
      return [];
    } finally {
      this.#loading = false;
    }
  }
}
